import { useState, useEffect } from 'react'
import { Plus, Calendar, MessageSquare, X, Send, Sparkles, Inbox, Clock, CheckCircle2, XCircle, MessageCircle } from 'lucide-react'
import { leaveAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'

const STATUS_STYLES: Record<string, { chip: string; dot: string; bar: string; icon: any }> = {
  PENDING:  { chip: 'bg-amber-50 text-amber-700 ring-amber-200',     dot: 'bg-amber-500',   bar: 'bg-amber-500',   icon: Clock },
  APPROVED: { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', bar: 'bg-emerald-500', icon: CheckCircle2 },
  REJECTED: { chip: 'bg-red-50 text-red-700 ring-red-200',           dot: 'bg-red-500',     bar: 'bg-red-500',     icon: XCircle },
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
}

const DEFAULT_STATUS = { chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200', dot: 'bg-neutral-400', bar: 'bg-neutral-400', icon: Clock }

export default function LeaveRequestPage() {
  const { success, error: toastError } = useToast()
  const [showForm, setShowForm] = useState(false)
  const [requests, setRequests] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({ target_date: '', reason: '' })

  const loadRequests = async () => {
    setIsLoading(true)
    try {
      const res = await leaveAPI.getMyRequests()
      setRequests(res.data.results || res.data)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load leave requests.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadRequests()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await leaveAPI.submitRequest({ target_date: formData.target_date, reason: formData.reason })
      success('Leave request submitted successfully.')
      setShowForm(false)
      setFormData({ target_date: '', reason: '' })
      await loadRequests()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to submit leave request.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass = "w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            My Requests
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Leave Requests
          </h1>
          <p className="text-sm text-neutral-500">Manage your leave applications</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="group flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5 self-start"
        >
          <Plus className="w-4 h-4 transition-transform group-hover:rotate-90" />
          New Request
        </button>
      </div>

      {/* ── Requests list ── */}
      {isLoading ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
          <p className="text-sm text-neutral-500">Loading leave requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
            <Inbox className="w-8 h-8 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-700">No leave requests yet</p>
          <p className="text-xs text-neutral-400">Submit your first request using the button above.</p>
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {requests.map((request) => {
            const style = STATUS_STYLES[request.status] || DEFAULT_STATUS
            const StatusIcon = style.icon
            const label = STATUS_LABELS[request.status] || request.status
            return (
              <div
                key={request.id}
                className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden hover:shadow-lg hover:shadow-neutral-200/50 hover:ring-neutral-300/70 transition-all duration-300"
              >
                {/* Left status accent bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.bar}`} />

                <div className="p-5 pl-6">
                  {/* Top row */}
                  <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-sm flex-shrink-0">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 truncate">
                          Leave Request
                        </p>
                        <p className="text-xs text-neutral-500 tabular-nums">
                          Applied for {request.target_date}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 whitespace-nowrap ${style.chip}`}>
                      <StatusIcon className="w-3 h-3" />
                      {label}
                    </span>
                  </div>

                  {/* Reason */}
                  <div className="bg-neutral-50 border border-neutral-100 rounded-xl px-3.5 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500 mb-0.5">
                      Reason
                    </p>
                    <p className="text-sm text-neutral-700 leading-relaxed">{request.reason}</p>
                  </div>

                  {/* Footer meta */}
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-neutral-500">
                    <span>
                      <span className="font-semibold text-neutral-600">Applied on:</span>{' '}
                      <span className="tabular-nums">{new Date(request.created_at).toLocaleDateString()}</span>
                    </span>
                    {request.admin_comment && (
                      <span className="inline-flex items-center gap-1.5 text-blue-700">
                        <MessageCircle className="w-3 h-3" />
                        <span className="font-semibold">Admin:</span> {request.admin_comment}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Modal Form ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  <Plus className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  New Leave Request
                </h2>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  Date <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="date"
                    name="target_date"
                    value={formData.target_date}
                    onChange={handleChange}
                    className={`${inputClass} pl-10`}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                  Reason <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    placeholder="Provide reason for leave..."
                    rows={4}
                    className={`${inputClass} pl-10 resize-none`}
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 rounded-xl font-medium text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium text-sm transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg disabled:opacity-50"
                >
                  <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}